import { CheckIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import { Search } from "lucide-react";
import * as React from "react";

import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDialogGuard, useIsInDialog } from "@/hooks/use-dialog-guard";
import { cn } from "@/lib/utils";

export type SearchableSelectOption = {
  value: string;
  /** Text shown in the list and on the trigger; also what the search matches. */
  label: string;
  /** Extra terms the search should match (ISIN, ticker, …). */
  keywords?: string[];
  /**
   * Heading rendered above the first of a run of consecutive options sharing
   * it. Also searchable, so typing the heading finds the whole group.
   */
  group?: string;
};

export type SearchableSelectProps = {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  /** Trigger text when nothing is selected. */
  placeholder?: string;
  /** Placeholder of the search field inside the dropdown. */
  searchPlaceholder?: string;
  /** Shown when `options` is empty. */
  emptyMessage?: string;
  /** Shown when the search term matches nothing. */
  noResultsMessage?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
};

/**
 * Every whitespace-separated term must appear somewhere in the option, in any
 * order — "emerging vanguard" has to find "Vanguard Emerging Markets", which a
 * single `includes` of the raw query would miss.
 */
function matches(option: SearchableSelectOption, terms: string[]): boolean {
  if (terms.length === 0) return true;
  const haystack = [option.label, option.group ?? "", ...(option.keywords ?? [])]
    .join(" ")
    .toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

/**
 * Single-select dropdown with a search field — the drop-in replacement for
 * `Select` once the option list grows past what is comfortable to scroll
 * (product registers, fund lists). Built on Popover rather than Radix Select
 * because Select owns typeahead keystrokes and cannot host a text input.
 */
export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select an option",
  searchPlaceholder = "Search…",
  emptyMessage = "No options available.",
  noResultsMessage = "No matches found.",
  id,
  disabled,
  className,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);
  const listRef = React.useRef<HTMLDivElement>(null);

  const term = search.trim().toLowerCase();
  const terms = React.useMemo(() => (term ? term.split(/\s+/) : []), [term]);
  const filtered = React.useMemo(
    () => options.filter((option) => matches(option, terms)),
    [options, terms],
  );

  const { markDirty } = useDialogGuard();
  const inDialog = useIsInDialog();
  const selected = options.find((option) => option.value === value);

  // Reset the highlight whenever the visible set changes, so Enter never
  // selects a row that scrolled out of the filtered list.
  React.useEffect(() => {
    setActiveIndex(0);
  }, [term]);

  React.useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  // Keep the highlighted row in view during arrow-key navigation.
  React.useEffect(() => {
    if (!open) return;
    const active = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    active?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  const select = (optionValue: string) => {
    // The single commit point for both paths — a click on the row and Enter on
    // the highlighted one. Only the click was ever visible from the DOM.
    markDirty();
    onValueChange(optionValue);
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (filtered.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % filtered.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + filtered.length) % filtered.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = filtered[activeIndex];
      if (option) select(option.value);
    }
  };

  // Modal inside a dialog only (QL-537): the list portals to <body>, outside
  // the Dialog, whose scroll lock then swallows wheel and touch scrolling over
  // it. A modal popover takes the lock itself, scoped to its own content. On a
  // plain page there is no lock to fight, so the page keeps scrolling.
  return (
    <Popover open={open} onOpenChange={setOpen} modal={inDialog}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "flex h-9 w-full min-w-0 items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-input bg-card py-2 pl-3 pr-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
        >
          <span className={cn("line-clamp-1 min-w-0", !selected && "text-muted-foreground")}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
        onKeyDown={handleKeyDown}
      >
        {/* The magnifier is the affordance, not the placeholder: a global rule
            (QL-103) clears placeholders on focus, and this field is focused the
            moment the dropdown opens. */}
        <div className="relative border-b">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-dialog-guard-ignore
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="rounded-none border-0 bg-transparent pl-9 shadow-none focus-visible:ring-0"
          />
        </div>
        {/* QL-534: the panel portals to <body>, outside any open Dialog, whose
            scroll lock cancels wheel and touch scrolling from a listener on
            `document`. Stopping both here keeps them from reaching it, so the
            list scrolls again. Not `modal`: that also blocks the dialog behind
            the panel, so a click on another control there only closed the
            picker and had to be repeated. */}
        <div
          ref={listRef}
          role="listbox"
          className="max-h-64 overflow-y-auto p-1"
          onWheel={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
        >
          {options.length === 0 ? (
            <p className="p-2 text-sm text-muted-foreground">{emptyMessage}</p>
          ) : filtered.length === 0 ? (
            <p className="p-2 text-sm text-muted-foreground">{noResultsMessage}</p>
          ) : (
            filtered.map((option, index) => (
              <React.Fragment key={option.value}>
                {/* A heading is not an option: it sits outside the arrow-key
                    sequence, which indexes `filtered` only. */}
                {option.group && option.group !== filtered[index - 1]?.group && (
                  <div
                    role="presentation"
                    className="px-2 pb-1 pt-2 text-xs font-medium text-muted-foreground"
                  >
                    {option.group}
                  </div>
                )}
                <button
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  data-active={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => select(option.value)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none",
                    index === activeIndex && "bg-accent text-accent-foreground",
                  )}
                >
                  <CheckIcon
                    className={cn("h-4 w-4 shrink-0", option.value !== value && "opacity-0")}
                  />
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                </button>
              </React.Fragment>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
